import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import '../common/cardView.scss';
import './quizCard.scss';
import { useHistory } from "react-router-dom";
import { reactGaEvent } from "sdk/actions/Common";
import { useTranslations, useQuizButtonStatus, useStyles } from "sdk/custom-hooks";
import { ReactComponent as TestImg } from "../../assets/images/evaluationTestIcon.svg";
import { Box, Button, Typography } from '@material-ui/core';
import { useTheme } from '../../constants/themeProvider';
import { ReactComponent as TryImage } from "../../assets/images/tryImage.svg";
import OverlayButton from '../../constants/OverlayButton';
import { toggleBottomBar } from 'sdk/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';

function QuizSuccessfulModal({ onClose, t }) {
    return (
        <Box className='modal-bg'>
            <Box className='modal-content' onClick={(e) => e.stopPropagation()}>
                <Box className='modal-heading'>
                    <h2 className='modal-heading-text'>{t('You have exceeded the number of quiz attempts!')}</h2>
                    <Box className='ok-align'>
                        <button type="button" onClick={onClose} className='ok-btn'>{t('Ok')}</button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

QuizSuccessfulModal.propTypes = {
    onClose: PropTypes.func,
    t: PropTypes.func,
};

function QuizCard(props) {
    const {
        quizData,
        singleTestSeriesLoading,
        onQuizCardClick,
        course_id,
        progressPercent,
        courseName,
        attemptsRemaining,
        quizTotalAttempts,
        isUserCertified,
        certificationToggle,
        showInstructionScreen
    } = props;
    const t = useTranslations();
    const history = useHistory();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { disabled, action } = useQuizButtonStatus(quizData);
    const quizAttempts = quizData?.quizAttempts || 0;
    const testScores = Array.isArray(quizData?.testScore) ? quizData.testScore : [];
    const hasAttempts = quizAttempts > 0;
    const bestScore = hasAttempts ? Math.max(...testScores, 0) : 0;
    const evaluationUnlockPercent = quizData?.testId?.EvaluationUnlockPercent || 0;
    const quizSlug = quizData?.testId?.slug;
    const isEvaluationLocked = Math.ceil(progressPercent) < evaluationUnlockPercent;
    const showUnlockHint = quizAttempts === 0 && progressPercent < evaluationUnlockPercent;
    const showUnlockedHint = quizAttempts === 0 && progressPercent >= evaluationUnlockPercent;
    const showPassedHint = quizAttempts > 0 && isUserCertified && certificationToggle;
    const showFailedHint = quizAttempts > 0 && isUserCertified === false && certificationToggle;
    const theme = useTheme();
    const location = useLocation();

    const styles = {
        startBtn: {
            textTransform: 'capitalize',
            fontWeight: '600',
            "&:hover": {
              background: "#0c3450e0 !important",
            }
        },
        buttonDisabled: {
            opacity: "0.4",
            cursor: "not-allowed",
          },
        viewResult: {
            textTransform: 'capitalize',
            fontWeight: '600',
            background: 'transparent !important',
            border: `1px solid ${theme?.primaryColor}`,
            borderRadius: '4px',
            color: `${theme?.primaryColor}`,
            "&:hover": {
                background: "#0000000a !important",
              }
        }
    }
    const classes = useStyles(styles)();
    const dispatch = useDispatch();
    const showInstructions = useSelector(state => state.filteredCourseList.showInstructions) || location?.state?.showInstructions;

    const hideModal = () => {
        setIsModalVisible(false);
    };

    useEffect(() => {
        if(showInstructions) {
            handleClickAction();
        }
    }, [showInstructions]);

    function handleClickAction() {
        if (action === 'PAYMENT') {
            history.push("/payments");
        }
        else if (action === 'START') {
            if (attemptsRemaining >=  quizTotalAttempts) {
                setIsModalVisible(true);
            }
            else {
            showInstructionScreen();
            onQuizCardClick(quizData.testId);
            reactGaEvent("Live Test", "Start Quiz");
            dispatch(toggleBottomBar(true));
            }
        } else {
            history.push('/tests/result/' + quizData._id);
        }
    }

    const showResult = ()=>{
        history.push(`/reports/${quizSlug}/${course_id}`,  {courseId:course_id, slug:quizSlug, pageType: "Instruction", courseName: courseName, redirectFromViewResult: true  })
      }

    function renderList() {
        return (
            <>
                <Box className='certificate-box' disabled={disabled}>
                    <Box className='scrollable-container'>
                        <Box>
                            {showUnlockHint && <Typography className='certifiedText'>*{t('Complete')} {evaluationUnlockPercent}% {t('of the course to unlock the evaluation test')}</Typography>}
                            {showUnlockedHint && <Typography className='certifiedText'>*{t('Your evaluation test is unlocked, you can now attempt the evaluation test.')}</Typography>}
                            {showPassedHint && <Typography className='certifiedTextPassed'>✅ {t('You have successfully passed the evaluation test. Great job!')}</Typography>}
                            {showFailedHint && <Typography className='certifiedText'>❌ {t('You did not pass the evaluation test. Review the course content and reattempt the test.')}</Typography>}
                            <Box className="certificate-text-div">
                                <Box className='certificate-main-div'>
                                    <Box className="certificate-default-img-container"><TestImg className='certificate-img'/></Box>
                                    <Box className='live-text'>
                                        <p className='genus-text'>{quizData?.testId?.name}</p>
                                        <p className='best-score'>{`${t("Best_Score")}: ${bestScore}/${quizData?.testId?.mark}`}</p>
                                    </Box>
                                </Box>
                                <Box className='completion-box'>
                                    <TryImage style={{marginRight: '-7px'}}/>
                                    <Typography variant='inherit' className='completion' style={{color: `${theme?.primaryColor}`}}>{`${quizAttempts}/${quizTotalAttempts}`} {t("Attempt")}</Typography>
                                    <OverlayButton
                                        onClick={handleClickAction}
                                        title={t("Get Started")}
                                        classStyleHeading={`${classes.startBtn} ${isEvaluationLocked ? classes.buttonDisabled : ""}`}
                                        disabled={isEvaluationLocked}
                                    />
                                    {hasAttempts && <Button onClick={showResult} className={classes.viewResult}>{t("view_result")}</Button>}
                                </Box>
                            </Box>
                            <Box className="certificate-text-div mobile-certificate-text-div">
                                <Box className='certificate-main-div'>
                                    <TestImg className='certificate-img'/>
                                    <Box className='live-text'>
                                        <p className='genus-text'>{quizData?.testId?.name}</p>
                                        <p className='best-score'>{`${t('Best_Score')}: ${bestScore}/${quizData?.testId?.mark}`}</p>
                                    </Box>
                                </Box>
                                <Box className='completion-box'>
                                    <TryImage style={{marginRight: '-7px'}}/>
                                    <Typography variant='inherit' className='completion' style={{color: `${theme?.primaryColor}`}}>{`${quizAttempts}/${quizTotalAttempts}`} {t("Attempt")}</Typography>
                                </Box>
                                <Box className="btn-container">
                                    <OverlayButton
                                        onClick={handleClickAction}
                                        title={t("Get Started")}
                                        classStyleHeading={`${classes.startBtn} ${isEvaluationLocked ? classes.buttonDisabled : ""}`}
                                        disabled={isEvaluationLocked}
                                    />
                                    {hasAttempts && <Button onClick={showResult} className={classes.viewResult}>{t("view_result")}</Button>}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {
                    isModalVisible &&
                    <QuizSuccessfulModal onClose={hideModal} t={t} />
                }
            </>
        );
    }

    if (singleTestSeriesLoading) {
        return null;
    }

    return renderList();
}

QuizCard.propTypes = {
    quizData: PropTypes.shape({
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        quizAttempts: PropTypes.number,
        testScore: PropTypes.arrayOf(PropTypes.number),
        testId: PropTypes.shape({
            _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            slug: PropTypes.string,
            name: PropTypes.string,
            mark: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            EvaluationUnlockPercent: PropTypes.number,
        }),
    }),
    singleTestSeriesLoading: PropTypes.bool,
    onQuizCardClick: PropTypes.func,
    course_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    progressPercent: PropTypes.number,
    courseName: PropTypes.string,
    attemptsRemaining: PropTypes.number,
    quizTotalAttempts: PropTypes.number,
    isUserCertified: PropTypes.bool,
    certificationToggle: PropTypes.bool,
    showInstructionScreen: PropTypes.func,
};

export default QuizCard;
